import { useState } from "react";
import { format } from "date-fns";
import { Calendar, Ban, Plus, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ReservationTimesEditor } from "./ReservationTimesEditor";
import { useDeleteReservation } from "@/hooks/useReservations";
import { toast } from "@/hooks/use-toast";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { canDeleteReservations } from "@/lib/propertyTabs";

type ReservationPayload = {
  id: string;
  start_date: string;
  end_date: string;
  source?: string | null;
  summary?: string | null;
  check_in_time?: string | null;
  check_out_time?: string | null;
  reservation_type?: string;
  [key: string]: unknown;
};

interface ReservationDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservation: ReservationPayload;
  propertyId: string;
  hasTask: boolean;
  onCreateTask?: () => void;
  creating?: boolean;
  /** Called after reservation is removed (drawer should close and clear selection). */
  onRemoved?: () => void;
}

export function ReservationDetailDrawer({
  open,
  onOpenChange,
  reservation,
  propertyId,
  hasTask,
  onCreateTask,
  creating = false,
  onRemoved,
}: ReservationDetailDrawerProps) {
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const deleteReservation = useDeleteReservation(propertyId);
  const ctx = usePropertyContext();
  const role = ctx?.role ?? (ctx?.isOwner ? "owner" : "cohost");
  const canRemove =
    ctx?.isOwner === true ||
    canDeleteReservations(role, ctx?.accessLevel ?? null, ctx?.permissions ?? null);

  const handleRemoveFromCalendar = async () => {
    if (!canRemove) {
      toast({
        title: "Only Full Access can remove reservations",
        description: "Ask the account owner if this reservation needs to be removed.",
        variant: "destructive",
      });
      return;
    }
    try {
      await deleteReservation.mutateAsync(reservation.id);
      toast({ title: "Removed", description: "Reservation removed. It won’t come back on Sync." });
      setRemoveConfirmOpen(false);
      onRemoved?.();
      onOpenChange(false);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : "Failed to remove reservation";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  const isBlocked =
    reservation.reservation_type === "blocked" ||
    (!!reservation.summary && String(reservation.summary).toLowerCase().includes("not available"));

  const nights = isBlocked
    ? null
    : Math.max(
        1,
        Math.round(
          (new Date(reservation.end_date).getTime() - new Date(reservation.start_date).getTime()) /
            (1000 * 60 * 60 * 24)
        )
      );

  const displayTitle = isBlocked
    ? (reservation.summary as string) || "Blocked"
    : (reservation.summary as string) ||
      (reservation.source ? `Reservation: ${reservation.source}` : "Reservation");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {displayTitle}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 flex flex-1 flex-col gap-4 overflow-y-auto">
          <div>
            <p className="font-medium text-foreground">
              {format(new Date(reservation.start_date), "MMM d, yyyy")} –{" "}
              {format(new Date(reservation.end_date), "MMM d, yyyy")}
            </p>
            {nights !== null && (
              <p className="text-sm text-muted-foreground">
                {nights} {nights === 1 ? "night" : "nights"}
                {reservation.source && !isBlocked && ` • ${reservation.source}`}
              </p>
            )}
          </div>

          {isBlocked ? (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground">
              <Ban className="h-5 w-5 shrink-0" />
              <p className="text-sm">Blocked dates do not require cleaning tasks.</p>
            </div>
          ) : (
            <>
              <ReservationTimesEditor
                reservationId={reservation.id}
                propertyId={propertyId}
                checkInTime={reservation.check_in_time ?? null}
                checkOutTime={reservation.check_out_time ?? null}
                guestCount={(reservation as { guest_count?: number | null }).guest_count ?? null}
              />
              {!hasTask && onCreateTask && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-4">
                  <p className="text-sm font-medium text-amber-800">No cleaning task for this stay.</p>
                  <Button
                    className="mt-3 w-full sm:w-auto"
                    onClick={onCreateTask}
                    disabled={creating}
                  >
                    <Plus className={`mr-2 h-4 w-4 ${creating ? "animate-spin" : ""}`} />
                    {creating ? "Creating…" : "Create Task"}
                  </Button>
                </div>
              )}
            </>
          )}

          {canRemove ? (
            <div className="mt-auto pt-4 border-t border-border">
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                onClick={() => setRemoveConfirmOpen(true)}
                disabled={deleteReservation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Remove from calendar
              </Button>
            </div>
          ) : null}
        </div>
      </SheetContent>

      <AlertDialog open={removeConfirmOpen} onOpenChange={setRemoveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this reservation?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the reservation and its open turnover from your schedule. Sync will not bring it back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFromCalendar}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteReservation.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
